import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { PermissionDenied } from "@/components/auth/PermissionDenied";
import { PageLoader } from "@/components/ui/loader";
import {
  canManageUsers,
  canManageMasters,
  canManageAssignmentRules,
} from "@/lib/permissions";
import { BranchesTab } from "@/components/admin/BranchesTab";
import { DepartmentsTab } from "@/components/admin/DepartmentsTab";
import { LocationsTab } from "@/components/admin/LocationsTab";
import { StoresTab } from "@/components/admin/StoresTab";
import { AssetCategoriesTab } from "@/components/admin/AssetCategoriesTab";
import { VendorsTab } from "@/components/admin/VendorsTab";
import { ChecklistsTab } from "@/components/admin/ChecklistsTab";
import { DynamicFieldsTab } from "@/components/admin/DynamicFieldsTab";
import { AssignmentRulesTab } from "@/components/admin/AssignmentRulesTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { RolesTab } from "@/components/admin/RolesTab";
import { RolePermissionMatrixTab } from "@/features/rbac/RolePermissionMatrixTab";
import { TemporaryAccessTab } from "@/features/rbac/TemporaryAccessTab";
import {
  Users,
  Shield,
  ShieldCheck,
  Key,
  Building,
  Network,
  MapPin,
  Store,
  Package,
  Truck,
  ClipboardCheck,
  GitBranch,
} from "lucide-react";

const adminSearchSchema = z.object({ tab: z.string().optional() });

export const Route = createFileRoute("/_authenticated/admin")({
  validateSearch: adminSearchSchema,
  component: () => (
    <RouteGuard module="admin_assignment_rules">
      <AdminPage />
    </RouteGuard>
  ),
});

function AdminPage() {
  const { data } = useCurrentUser();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/admin" });
  const roles = data?.roles ?? [];

  const showUsers = canManageUsers(roles);
  const showMasters = canManageMasters(roles);
  const showAssignment = canManageAssignmentRules(roles);

  const defaultTab = useMemo(() => {
    if (showUsers) return "users";
    if (showAssignment) return "assignment-rules";
    return "users";
  }, [showUsers, showAssignment]);

  const [tab, setTab] = useState<string>(search.tab ?? defaultTab);

  useEffect(() => {
    if (search.tab && search.tab !== tab) setTab(search.tab);
  }, [search.tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // If a non-super-admin lands on a restricted tab, fall back.
  useEffect(() => {
    if (!data) return;
    const restricted: Record<string, boolean> = {
      users: !showUsers,
      roles: !showUsers,
      "role-permissions": !showUsers,
      "temporary-access": !showUsers,
      branches: !showMasters,
      departments: !showMasters,
      locations: !showMasters,
      "asset-categories": !showMasters,
      vendors: !showMasters,
      checklists: !showMasters,
      "dynamic-fields": !showMasters,
      "assignment-rules": !showAssignment,
    };
    if (restricted[tab]) {
      setTab(defaultTab);
      navigate({ to: "/admin", search: { tab: defaultTab } });
    }
  }, [tab, data, showUsers, showMasters, showAssignment, defaultTab, navigate]);

  if (!data) return <PageLoader />;
  if (!showUsers && !showMasters && !showAssignment) {
    return <PermissionDenied />;
  }

  return (
    <div className="space-y-6 p-4 md:p-8 pb-16">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-b pb-5">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Admin</h1>
          <p className="text-sm text-muted-foreground">
            {showUsers
              ? "Manage users, organisation, location hierarchy, asset master, vendors and checklists."
              : "Manage work order assignment rules for your scope."}
          </p>
        </div>
      </div>
      <Tabs
        value={tab}
        onValueChange={(val) => {
          setTab(val);
          navigate({ to: "/admin", search: { tab: val } });
        }}
        className="space-y-4"
      >
        <TabsList className="flex flex-wrap h-auto gap-1">
          {showUsers && (
            <TabsTrigger value="users" className="gap-1.5">
              <Users className="h-3.5 w-3.5" />
              <span>Users &amp; Roles</span>
            </TabsTrigger>
          )}
          {showUsers && (
            <TabsTrigger value="roles" className="gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              <span>Roles</span>
            </TabsTrigger>
          )}
          {showUsers && (
            <TabsTrigger value="role-permissions" className="gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Role Permissions</span>
            </TabsTrigger>
          )}
          {showUsers && (
            <TabsTrigger value="temporary-access" className="gap-1.5">
              <Key className="h-3.5 w-3.5" />
              <span>Temporary Access</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="branches" className="gap-1.5">
              <Building className="h-3.5 w-3.5" />
              <span>Branches</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="departments" className="gap-1.5">
              <Network className="h-3.5 w-3.5" />
              <span>Departments</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="locations" className="gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              <span>Locations</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="stores" className="gap-1.5">
              <Store className="h-3.5 w-3.5" />
              <span>Stores</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="asset-categories" className="gap-1.5">
              <Package className="h-3.5 w-3.5" />
              <span>Asset Master</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="vendors" className="gap-1.5">
              <Truck className="h-3.5 w-3.5" />
              <span>Vendors</span>
            </TabsTrigger>
          )}
          {showMasters && (
            <TabsTrigger value="checklists" className="gap-1.5">
              <ClipboardCheck className="h-3.5 w-3.5" />
              <span>Checklists</span>
            </TabsTrigger>
          )}
          {showAssignment && (
            <TabsTrigger value="assignment-rules" className="gap-1.5">
              <GitBranch className="h-3.5 w-3.5" />
              <span>WO Assignment</span>
            </TabsTrigger>
          )}
        </TabsList>
        {showUsers && <TabsContent value="users" className="w-full pt-2"><UsersTab /></TabsContent>}
        {showUsers && <TabsContent value="roles" className="w-full pt-2"><RolesTab /></TabsContent>}
        {showUsers && <TabsContent value="role-permissions" className="w-full pt-2"><RolePermissionMatrixTab /></TabsContent>}
        {showUsers && <TabsContent value="temporary-access" className="w-full pt-2"><TemporaryAccessTab /></TabsContent>}
        {showMasters && <TabsContent value="branches" className="w-full pt-2"><BranchesTab /></TabsContent>}
        {showMasters && <TabsContent value="departments" className="w-full pt-2"><DepartmentsTab /></TabsContent>}
        {showMasters && <TabsContent value="locations" className="w-full pt-2"><LocationsTab /></TabsContent>}
        {showMasters && <TabsContent value="stores" className="w-full pt-2"><StoresTab /></TabsContent>}
        {showMasters && <TabsContent value="asset-categories" className="w-full pt-2"><AssetCategoriesTab /></TabsContent>}
        {showMasters && <TabsContent value="vendors" className="w-full pt-2"><VendorsTab /></TabsContent>}
        {showMasters && <TabsContent value="checklists" className="w-full pt-2"><ChecklistsTab /></TabsContent>}
        {/* {showMasters && <TabsContent value="dynamic-fields" className="w-full pt-2"><DynamicFieldsTab /></TabsContent>} */}
        {showAssignment && <TabsContent value="assignment-rules" className="w-full pt-2"><AssignmentRulesTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
